import Toast from "components/cms/Toast";
import SectionCard from "components/cms/SectionCard";
import {
  AreaInput,
  CardRow,
  FieldRow,
  FileInput,
  GroupBox,
  TextInput,
} from "components/cms/fields";
import LogosSection from "components/cms/LogosSection";
import useCmsPage from "hooks/useCmsPage";
import {
  addHomepage,
  deleteHomepage,
  getHomepage,
  prepareHomepage,
  updateHomepage,
} from "api/homepage";
import { emptyHome } from "utils/defaults";

const HERO_KEYS = [
  "heroTag",
  "heroTitle1",
  "heroTitle2",
  "heroDescription",
  "heroPrimaryCtaText",
  "heroPrimaryCtaLink",
  "heroSecondaryCtaText",
  "heroSecondaryCtaLink",
  "heroTrustText",
  "heroLogoCard",
  "heroCard1",
  "heroCard2",
  "heroSectionHide",
];
const SERVICES_KEYS = [
  "servicesTitle",
  "servicesSubtitle",
  "services",
  "servicesFooterText",
  "servicesCtaText",
  "servicesCtaLink",
  "servicesHide",
];
const PROCESS_KEYS = [
  "processTitle",
  "processSubTitle",
  "processSteps",
  "processStep1BtnText",
  "processStep1BtnLink",
  "processHide",
];
const INSIGHTS_KEYS = [
  "insightsTitle",
  "insightsCtaText",
  "insightsCtaLink",
  "insight1",
  "insight2",
  "insight3",
  "insightsHide",
];

const featureCard = (label, path, form, setField, frMap) => (
  <GroupBox title={label}>
    <TextInput
      label="Title"
      path={`${path}.title`}
      form={form}
      setField={setField}
      frMap={frMap}
    />
    <AreaInput
      label="Description"
      path={`${path}.description`}
      form={form}
      setField={setField}
      frMap={frMap}
    />
  </GroupBox>
);

const insightCard = (label, path, form, setField, frMap) => (
  <GroupBox title={label}>
    <TextInput
      label="Title line 1"
      path={`${path}.title1`}
      form={form}
      setField={setField}
      frMap={frMap}
    />
    <TextInput
      label="Title line 2"
      path={`${path}.title2`}
      form={form}
      setField={setField}
      frMap={frMap}
    />
    <AreaInput
      label="Description"
      path={`${path}.description`}
      form={form}
      setField={setField}
      frMap={frMap}
    />
    <TextInput
      label="Link"
      path={`${path}.link`}
      form={form}
      setField={setField}
    />
    <TextInput
      label="Type"
      path={`${path}.type`}
      form={form}
      setField={setField}
    />
  </GroupBox>
);

export default function HomePage() {
  const cms = useCmsPage({
    load: getHomepage,
    create: addHomepage,
    update: updateHomepage,
    remove: deleteHomepage,
    empty: emptyHome,
    mapResponse: (res) => res.data?.data,
    prepare: prepareHomepage,
  });
  const {
    form,
    setField,
    frMap,
    exists,
    loading,
    toast,
    setToast,
    saveSection,
    savingSection,
    isSectionDirty,
  } = cms;

  const saveHome = (sectionId, keys) => {
    if (!exists) {
      setToast({
        type: "error",
        message: "Home page is not loaded yet. Refresh and try again.",
        id: Date.now(),
      });
      return;
    }
    saveSection(sectionId, keys);
  };

  if (loading) {
    return <p className="mt-5 text-gray-600">Loading Home…</p>;
  }

  return (
    <div className="mt-3">
      <Toast toast={toast} onClose={() => setToast(null)} />

      <SectionCard
        title="Hero"
        hide={form.heroSectionHide}
        hideId="heroSectionHide"
        onHideChange={(value) => setField("heroSectionHide", value)}
        onSave={() => saveHome("hero", HERO_KEYS)}
        saving={savingSection === "hero"}
        canSave={isSectionDirty(HERO_KEYS)}
      >
        <FieldRow>
          <TextInput label="Tag" path="heroTag" form={form} setField={setField} frMap={frMap} />
        </FieldRow>
        <FieldRow>
          <TextInput
            label="Title line 1"
            path="heroTitle1"
            form={form}
            setField={setField}
            frMap={frMap}
          />
          <TextInput
            label="Title line 2"
            path="heroTitle2"
            form={form}
            setField={setField}
            frMap={frMap}
          />
        </FieldRow>
        <AreaInput
          label="Description"
          path="heroDescription"
          form={form}
          setField={setField}
          frMap={frMap}
        />
        <FieldRow>
          <TextInput
            label="Primary CTA text"
            path="heroPrimaryCtaText"
            form={form}
            setField={setField}
            frMap={frMap}
          />
          <TextInput
            label="Primary CTA link"
            path="heroPrimaryCtaLink"
            form={form}
            setField={setField}
          />
        </FieldRow>
        <FieldRow>
          <TextInput
            label="Secondary CTA text"
            path="heroSecondaryCtaText"
            form={form}
            setField={setField}
            frMap={frMap}
          />
          <TextInput
            label="Secondary CTA link"
            path="heroSecondaryCtaLink"
            form={form}
            setField={setField}
          />
        </FieldRow>
        <FieldRow>
          <TextInput
            label="Trust text"
            path="heroTrustText"
            form={form}
            setField={setField}
            frMap={frMap}
          />
        </FieldRow>
        <FileInput
          label="Logo card image"
          path="heroLogoCard.image"
          form={form}
          setField={setField}
        />
        <FieldRow>
          <TextInput
            label="Logo card text"
            path="heroLogoCard.text"
            form={form}
            setField={setField}
            frMap={frMap}
          />
        </FieldRow>
        <CardRow>
          {featureCard("Hero card 1", "heroCard1", form, setField, frMap)}
          {featureCard("Hero card 2", "heroCard2", form, setField, frMap)}
        </CardRow>
      </SectionCard>

      <SectionCard
        title="Services"
        hide={form.servicesHide}
        hideId="servicesHide"
        onHideChange={(value) => setField("servicesHide", value)}
        onSave={() => saveHome("services", SERVICES_KEYS)}
        saving={savingSection === "services"}
        canSave={isSectionDirty(SERVICES_KEYS)}
      >
        <FieldRow>
          <TextInput
            label="Title"
            path="servicesTitle"
            form={form}
            setField={setField}
            frMap={frMap}
          />
          <TextInput
            label="Subtitle"
            path="servicesSubtitle"
            form={form}
            setField={setField}
            frMap={frMap}
          />
        </FieldRow>
        <CardRow>
          {(form.services || []).map((_, index) => (
            <GroupBox key={`service-${index}`} title={`Service ${index + 1}`}>
              <TextInput
                label="Title"
                path={`services.${index}.title`}
                form={form}
                setField={setField}
                frMap={frMap}
              />
              <AreaInput
                label="Description"
                path={`services.${index}.description`}
                form={form}
                setField={setField}
                frMap={frMap}
              />
            </GroupBox>
          ))}
        </CardRow>
        <AreaInput
          label="Footer text"
          path="servicesFooterText"
          form={form}
          setField={setField}
          frMap={frMap}
        />
        <FieldRow>
          <TextInput
            label="CTA text"
            path="servicesCtaText"
            form={form}
            setField={setField}
            frMap={frMap}
          />
          <TextInput
            label="CTA link"
            path="servicesCtaLink"
            form={form}
            setField={setField}
          />
        </FieldRow>
      </SectionCard>

      <LogosSection />

      <SectionCard
        title="Process"
        hide={form.processHide}
        hideId="processHide"
        onHideChange={(value) => setField("processHide", value)}
        onSave={() => saveHome("process", PROCESS_KEYS)}
        saving={savingSection === "process"}
        canSave={isSectionDirty(PROCESS_KEYS)}
      >
        <FieldRow>
          <TextInput
            label="Title"
            path="processTitle"
            form={form}
            setField={setField}
            frMap={frMap}
          />
          <TextInput
            label="Subtitle"
            path="processSubTitle"
            form={form}
            setField={setField}
            frMap={frMap}
          />
        </FieldRow>
        <FieldRow>
          <TextInput
            label="Step 1 button text"
            path="processStep1BtnText"
            form={form}
            setField={setField}
            frMap={frMap}
          />
          <TextInput
            label="Step 1 button link"
            path="processStep1BtnLink"
            form={form}
            setField={setField}
          />
        </FieldRow>
        <CardRow>
          {(form.processSteps || []).map((_, index) => (
            <GroupBox key={`process-${index}`} title={`Step ${index + 1}`}>
              <TextInput
                label="Step number"
                path={`processSteps.${index}.step`}
                form={form}
                setField={(path, value) =>
                  setField(path, value === "" ? "" : Number(value) || value)
                }
                type="number"
              />
              <TextInput
                label="Title"
                path={`processSteps.${index}.title`}
                form={form}
                setField={setField}
                frMap={frMap}
              />
              <AreaInput
                label="Description"
                path={`processSteps.${index}.description`}
                form={form}
                setField={setField}
                frMap={frMap}
              />
            </GroupBox>
          ))}
        </CardRow>
      </SectionCard>

      <SectionCard
        title="Insights"
        hide={form.insightsHide}
        hideId="insightsHide"
        onHideChange={(value) => setField("insightsHide", value)}
        onSave={() => saveHome("insights", INSIGHTS_KEYS)}
        saving={savingSection === "insights"}
        canSave={isSectionDirty(INSIGHTS_KEYS)}
      >
        <FieldRow>
          <TextInput
            label="Title"
            path="insightsTitle"
            form={form}
            setField={setField}
            frMap={frMap}
          />
        </FieldRow>
        <FieldRow>
          <TextInput
            label="CTA text"
            path="insightsCtaText"
            form={form}
            setField={setField}
            frMap={frMap}
          />
          <TextInput
            label="CTA link"
            path="insightsCtaLink"
            form={form}
            setField={setField}
          />
        </FieldRow>
        <CardRow>
          {insightCard("Insight 1", "insight1", form, setField, frMap)}
          {insightCard("Insight 2", "insight2", form, setField, frMap)}
          {insightCard("Insight 3", "insight3", form, setField, frMap)}
        </CardRow>
      </SectionCard>
    </div>
  );
}
